<script setup lang="ts">
import type { ResponsiveImage } from '@/types/Product';
import OrderOverviewSelection from './OrderOverviewSelection.vue';
import ExpandableDescription from './ExpandableDescription.vue';
import type { RoomTypeImage } from '@/types/schemas/nbc';
import { formatPrice } from '@/utils/formatting/currencyFormatter';
import { $t } from '@/composables/translations/useTranslate';
import { computed } from 'vue';
import { HIDE_REBATES_CHECKOUT } from '@/constants/checkout/config';
import type { SeparateItem, AuxService } from '@/utils/checkout/pricingExtractor';

interface RoomSelection {
  roomTypeName: string;
  passengersLabel?: string;
  price?: number;
  boardName?: string;
  boardDescription?: string | null;
  separateItems?: readonly SeparateItem[];
  roomCode?: string;
  images?: RoomTypeImage[];
  description?: string | null;
}

interface Props {
  hotelImage?: ResponsiveImage;
  hotelImageUrl?: string;
  hotelName?: string;
  hotelLocation?: string;
  organizerName?: string;
  travelType?: string;
  notes?: string[];
  checkIn?: string;
  checkOut?: string;
  nights?: number;
  rooms?: RoomSelection[];
  /** Auxiliary services (childcare, service fee, …) shown below room & board. */
  auxServices?: readonly AuxService[];
  totalPrice?: number;
  showEditButton?: boolean;
  /** Mobile expanded view: inset the hero image with padding + full rounding instead of full-bleed. */
  paddedImage?: boolean;
  hasPriceDeviation?: boolean;
  priceDeviationAmount?: number;
  originalTotalPrice?: number;
  hasDiscount?: boolean;
  discountAmount?: number;
  discountPercent?: number;
  /** Distinct rebate names, listed in the badge tooltip. */
  discountReasons?: readonly string[];
  discountOriginalPrice?: number;
}

const props = withDefaults(defineProps<Props>(), {
  hotelImage: undefined,
  hotelImageUrl: undefined,
  hotelName: undefined,
  hotelLocation: undefined,
  organizerName: undefined,
  travelType: undefined,
  notes: () => [],
  checkIn: undefined,
  checkOut: undefined,
  nights: undefined,
  rooms: () => [],
  auxServices: () => [],
  totalPrice: undefined,
  showEditButton: false,
  paddedImage: false,
  hasPriceDeviation: false,
  priceDeviationAmount: undefined,
  originalTotalPrice: undefined,
  hasDiscount: false,
  discountAmount: undefined,
  discountPercent: undefined,
  discountReasons: () => [],
  discountOriginalPrice: undefined,
});

const partnerLabel = computed(() => {
  const prefix = (props.travelType ?? '').trim().toUpperCase();

  if (prefix.startsWith('VV')) {
    return $t('ibe.checkout.order-summary.intermediary', 'Intermediary:');
  }

  return $t('ibe.checkout.order-summary.organizer', 'Organizer:');
});

const deviationText = computed(() => {
  if (!props.priceDeviationAmount) return '';

  const difference = formatPrice(Math.abs(props.priceDeviationAmount), true);

  if (props.priceDeviationAmount > 0) {
    return $t(
      'ibe.checkout.price-deviation.total-increase',
      'The total price has increased by {difference} since you viewed the offer.',
      { difference }
    );
  } else {
    return $t(
      'ibe.checkout.price-deviation.total-decrease',
      'The total price has decreased by {difference} since you viewed the offer.',
      { difference }
    );
  }
});

const isPriceCheaper = computed(() => (props.priceDeviationAmount ?? 0) < 0);
const deviationTextClass = computed(() => (isPriceCheaper.value ? 'text-green-600' : 'text-error'));
const deviationIconClass = computed(() => (isPriceCheaper.value ? 'bg-green-600' : 'bg-error'));

const discountReason = computed(() => props.discountReasons.join(', '));

const discountAriaLabel = computed(() => {
  // Use plainText=true for aria-label (no nbsp/non-breaking hyphen)
  const savingsAmount = props.discountAmount
    ? formatPrice(Math.abs(props.discountAmount), true, true)
    : '';

  if (!props.discountPercent) {
    return $t(
      'ibe.checkout.discount.aria-label-no-percent',
      'discount ({reasons}), saving {amount}',
      { reasons: discountReason.value, amount: savingsAmount }
    );
  }

  if (discountReason.value && savingsAmount) {
    return $t(
      'ibe.checkout.discount.aria-label-full',
      'up to {percent} percent discount ({reason}), saving {amount}',
      {
        percent: props.discountPercent,
        reason: discountReason.value,
        amount: savingsAmount,
      }
    );
  }

  if (discountReason.value) {
    return $t(
      'ibe.checkout.discount.aria-label-with-reason',
      'up to {percent} percent discount ({reason})',
      {
        percent: props.discountPercent,
        reason: discountReason.value,
      }
    );
  }

  if (savingsAmount) {
    return $t(
      'ibe.checkout.discount.aria-label-with-amount',
      'up to {percent} percent discount, saving {amount}',
      {
        percent: props.discountPercent,
        amount: savingsAmount,
      }
    );
  }

  return $t('ibe.checkout.discount.aria-label-basic', 'up to {percent} percent discount', {
    percent: props.discountPercent,
  });
});

const emit = defineEmits<{
  edit: [];
}>();

function handleEdit() {
  emit('edit');
}
</script>

<template>
  <div
    class="flex flex-col lg:max-w-[435px]"
    :class="props.paddedImage ? 'bg-white rounded-[10px] p-5' : ''"
  >
    <div
      v-if="props.hotelImageUrl"
      class="w-full overflow-hidden"
      :class="props.paddedImage ? 'h-[180px] rounded-[10px] mb-5' : 'h-[250px] rounded-t-[10px]'"
    >
      <img
        :src="props.hotelImageUrl"
        :alt="props.hotelName || 'Hotel'"
        class="block w-full h-full object-cover"
        loading="lazy"
      />
    </div>
    <div
      v-else
      class="bg-actions-tertiary w-full animate-pulse"
      :class="props.paddedImage ? 'h-[180px] rounded-[10px] mb-5' : 'h-[250px] rounded-t-[10px]'"
      aria-hidden="true"
    ></div>

    <div
      class="flex flex-col"
      :class="props.paddedImage ? '' : 'p-5 bg-white rounded-b-[10px]'"
    >
      <p v-if="props.hotelName" class="text-text-primary text-body-large mb-2.5">
        {{ props.hotelName }}
      </p>
      <p v-if="props.hotelLocation" class="text-body mb-[5px]">{{ props.hotelLocation }}</p>
      <p v-if="props.organizerName" class="mb-5 text-body">
        {{ partnerLabel }} {{ props.organizerName }}
      </p>

      <div
        v-if="props.notes && props.notes.length > 0"
        class="rounded-xl px-2.5 py-3.5 bg-highlight-2 flex items-start gap-2.5 mb-7.5"
      >
        <i class="bg-black h-9 w-15 icon icon-info"></i>
        <div class="flex flex-col gap-2">
          <p v-for="(hint, i) in props.notes" :key="i" class="text-body">
            {{ hint }}
          </p>
        </div>
      </div>

      <div v-if="props.checkIn || props.checkOut || props.nights" class="flex mb-7.5">
        <div
          v-if="props.checkIn"
          class="flex-1 flex flex-col gap-1.5 pr-4 border-r border-opacity-50"
        >
          <p class="text-body-small">{{ $t('ibe.checkout.order-summary.check-in', 'Check-in') }}</p>
          <p class="text-body-large">{{ props.checkIn }}</p>
        </div>
        <div
          v-if="props.checkOut"
          :class="[
            'flex-1 flex flex-col gap-1.5 px-4',
            props.nights ? 'border-r border-opacity-50' : '',
          ]"
        >
          <p class="text-body-small">
            {{ $t('ibe.checkout.order-summary.check-out', 'Check-out') }}
          </p>
          <p class="text-body-large">{{ props.checkOut }}</p>
        </div>
        <div v-if="props.nights" class="flex-1 flex flex-col gap-1.5 pl-4">
          <p class="text-body-small">{{ $t('ibe.checkout.order-summary.duration', 'Duration') }}</p>
          <p class="text-body-large">{{ props.nights }}</p>
        </div>
      </div>

      <div class="flex flex-col">
        <p class="text-body-small mb-[5px]">
          {{ $t('ibe.checkout.order-summary.rooms-board', 'Rooms & Board') }}
        </p>

        <template v-for="(room, index) in props.rooms" :key="`room-${index}`">
          <OrderOverviewSelection
            :included="true"
            :name="room.roomTypeName"
            :name-label="room.passengersLabel"
            :show-price-label="true"
            :price="room.price"
          >
            <ExpandableDescription v-if="room.description" :html="room.description" />
          </OrderOverviewSelection>

          <!-- Board: name only, no price -->
          <OrderOverviewSelection v-if="room.boardName" :included="true" :name="room.boardName">
            <ExpandableDescription v-if="room.boardDescription" :text="room.boardDescription" />
          </OrderOverviewSelection>

          <!-- Separate-on-documents items (e.g. Endreinigung), shown with a price -->
          <OrderOverviewSelection
            v-for="(item, sIndex) in room.separateItems"
            :key="`sep-${index}-${sIndex}`"
            :included="true"
            :name="item.label"
            :price="item.amount"
          />
        </template>

        <!-- Auxiliary services (childcare, service fee, …): price or "incl." -->
        <OrderOverviewSelection
          v-for="(aux, aIndex) in props.auxServices"
          :key="`aux-${aIndex}`"
          :included="true"
          :name="aux.label"
          :price="aux.included ? undefined : aux.price"
          :included-text="aux.included ? $t('ibe.checkout.summary.included', 'incl.') : undefined"
        />

        <div
          v-if="!HIDE_REBATES_CHECKOUT && props.hasDiscount"
          class="flex items-center justify-end mt-2.5 mb-2.5"
        >
          <VTooltip v-if="props.discountReasons.length > 0" placement="top">
            <span
              class="inline-flex items-center gap-1 px-2 py-1 bg-text-highlight-2 text-white text-body-small cursor-help"
              :aria-label="discountAriaLabel"
            >
              <template v-if="props.discountPercent">
                {{ $t('ibe.checkout.discount.badge-prefix', 'up to') }}
                <strong>{{ props.discountPercent }}%</strong>
              </template>
              <template v-else>
                {{ $t('ibe.checkout.discount.badge-no-percent', 'Your benefit') }}
              </template>
              <i class="w-4 h-4 ml-1 bg-white icon icon-info"></i>
            </span>

            <template #popper>
              <p v-for="reason in props.discountReasons" :key="reason">{{ reason }}</p>
            </template>
          </VTooltip>
          <span
            v-else
            class="px-2 py-1 bg-text-highlight-2 text-white text-body-small"
            :aria-label="discountAriaLabel"
          >
            <template v-if="props.discountPercent">
              {{ $t('ibe.checkout.discount.badge-prefix', 'up to') }}
              <strong>{{ props.discountPercent }}%</strong>
            </template>
            <template v-else>
              {{ $t('ibe.checkout.discount.badge-no-percent', 'Your benefit') }}
            </template>
          </span>
        </div>

        <p
          v-if="!HIDE_REBATES_CHECKOUT && props.hasDiscount && props.discountOriginalPrice"
          class="ml-auto text-body-small line-through"
        >
          {{ $t('ibe.checkout.discount.original-price', 'Original price:') }}
          {{ formatPrice(props.discountOriginalPrice, true) }}
        </p>

        <p
          v-if="props.totalPrice"
          class="ml-auto font-bold"
          :class="props.hasPriceDeviation ? deviationTextClass : ''"
        >
          {{ $t('ibe.checkout.order-summary.total', 'Total:') }}
          {{ formatPrice(props.totalPrice, true) }}
        </p>

        <!-- Price Deviation Indicator with Tooltip -->
        <VTooltip
          v-if="props.hasPriceDeviation && props.priceDeviationAmount"
          placement="top"
          class="inline-flex ml-auto"
        >
          <button
            type="button"
            class="flex items-center mt-1 mb-5 text-body-small cursor-help"
            :class="deviationTextClass"
          >
            {{ $t('ibe.checkout.price-deviation.label', 'Price deviation') }}
            <i class="w-5 h-5 ml-1 icon icon-info" :class="deviationIconClass"></i>
          </button>

          <template #popper>
            <p>{{ deviationText }}</p>
          </template>
        </VTooltip>

        <div v-else class="mb-5"></div>

        <button v-if="props.showEditButton" class="btn-tertiary print:hidden" @click="handleEdit">
          {{ $t('ibe.checkout.edit', 'Edit') }} <i class="w-5 h-5 bg-black icon icon-pen"></i>
        </button>
      </div>
    </div>
  </div>
</template>
